/* ============================================================================
 * DEPRECATED — DO NOT MODIFY.
 *
 * This file is part of the legacy V1 playback pipeline. The V2 engine
 * (src/Studio/engine/PlaybackEngineV2.ts) is the intended runtime and is
 * being migrated to sole-runtime status.
 *
 * Rules (enforced by memory/feedback_v1_deprecation.md):
 *   - Bug fixes on playback go in V2 or as a V2 port. Do not patch here.
 *   - New features do not land here.
 *   - Only the removal phase itself may edit this file.
 *
 * Migration status: Phase 4 in progress. See
 *   src/Studio/report/2026-04-17__playback-v2-phase1-audio-prime.md
 * ============================================================================ */

/**
 * Timeline Engine - Video/Audio Sync  (LEGACY V1)
 *
 * CLEAN REBUILD: This file focuses ONLY on video/audio playback synchronization.
 * All transition logic has been moved to src/Studio/transitions/
 *
 * Responsibilities:
 * - Sync video element currentTime with timeline playhead
 * - Handle playback rate corrections for smooth playback
 * - Sync audio elements and volume
 * - Simple visibility based on clip timing (no transition logic)
 */

import type { Clip, Asset, Track, Transition } from "./EditorStore";
import type { RegionEffect } from "./AudioManager";
import { getRampedValue } from "./ramp";
import { ClipIntervalCache } from "./clipIntervalCache";
import { getCachedFrameNear } from "../engine/VideoRegistry";
import { setScrubBitmap, clearScrubBitmap } from "../engine/ScrubBitmapCache";

// Re-export audio manager functions
export {
  syncAudioClipsAtPlayhead,
  type AudioMap,
  getActiveAudioElements,
  cleanupUnusedAudioElements,
  resetAudioSyncState,
  getOrCreateAudioElement,
  markAudioPlaybackStart,
} from "./AudioManager";

// ==================== SIMPLE UTILITIES ====================

/**
 * Check if playhead is inside clip time range.
 * Simple and exact - no hold windows, no transitions.
 */
export function isTimeInsideClip(clip: Clip, t: number): boolean {
  return t + PLAYHEAD_WINDOW_EPS >= clip.start && t <= clip.end + PLAYHEAD_WINDOW_EPS;
}

/**
 * Compute the source media time for a given clip at timeline time t.
 * Applies clip.in offset and optional speed.
 */
export function mediaTimeForClip(clip: Clip, t: number): number {
  const speed = (clip as any).speed ?? 1;
  const rel = Math.max(0, t - clip.start);
  return clip.in + rel * speed;
}

// ==================== LEGACY TYPE EXPORTS (for backward compatibility) ====================

/**
 * @deprecated Use TransitionEffect from '../transitions' instead
 */
export type TransitionEffect = {
  opacity: number;
  offsetX: number;
  offsetY: number;
  scale: number;
  clipProgress: number;
  transitionType: Transition["type"] | null;
  isTransitioning: boolean;
};

// ==================== VIDEO MAP TYPE ====================

export type VideoMap = Map<string, HTMLVideoElement>;

// ==================== VIDEO SYNC STATE ====================

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const PLAYHEAD_WINDOW_EPS = 1 / 240;

// Per-video sync state for smooth playback
const videoSyncState = {
  lastSeekTime: new Map<string, number>(),
  prevPlayhead: 0,
};

// Track how many consecutive frames each video has been inactive,
// to debounce mute at clip boundaries (prevents single-frame dropout).
const inactiveFrameCount = new Map<string, number>();
const MUTE_DEBOUNCE_FRAMES = 2;

/**
 * Reset video sync state. Call on major state changes.
 */
export function resetVideoSyncState() {
  videoSyncState.lastSeekTime.clear();
  videoSyncState.prevPlayhead = 0;
  regionVolumeRamp.clear();
  regionSpeedRamp.clear();
  inactiveFrameCount.clear();
}

/** Prune ramp Maps to prevent unbounded growth over long sessions. */
const MAX_RAMP_ENTRIES = 128;
function pruneRampMaps() {
  if (regionVolumeRamp.size > MAX_RAMP_ENTRIES) regionVolumeRamp.clear();
  if (regionSpeedRamp.size > MAX_RAMP_ENTRIES) regionSpeedRamp.clear();
}

// Region FX ramp state
const DEFAULT_VOL_RAMP_MS = 120;
const DEFAULT_SPEED_RAMP_MS = 200;

const volumeRampMsFor = (regionEffect?: RegionEffect | null) => {
  const base = regionEffect?.rampMs;
  if (typeof base === "number" && Number.isFinite(base)) return clamp(base, 100, 150);
  return DEFAULT_VOL_RAMP_MS;
};

const speedRampMsFor = (regionEffect?: RegionEffect | null) => {
  const base = regionEffect?.rampMs;
  if (typeof base === "number" && Number.isFinite(base)) return clamp(base, 150, 250);
  return DEFAULT_SPEED_RAMP_MS;
};

const regionVolumeRamp = new Map<string, import("./ramp").RampState>();
const regionSpeedRamp = new Map<string, import("./ramp").RampState>();

// ==================== VOLUME FADE CALCULATION ====================

function calculateVolumeWithFades(
  clip: Clip,
  playhead: number,
  baseVolume: number
): number {
  const fadeIn = (clip as any).fadeInDuration ?? 0;
  const fadeOut = (clip as any).fadeOutDuration ?? 0;
  const relTime = playhead - clip.start;
  const clipDuration = clip.end - clip.start;
  const remaining = clipDuration - relTime;

  let fadeMultiplier = 1;

  if (fadeIn > 0 && relTime < fadeIn) {
    fadeMultiplier = Math.min(fadeMultiplier, relTime / fadeIn);
  }
  if (fadeOut > 0 && remaining < fadeOut) {
    fadeMultiplier = Math.min(fadeMultiplier, remaining / fadeOut);
  }

  return baseVolume * Math.max(0, Math.min(1, fadeMultiplier));
}

// ==================== Sync Caches ====================
// Pre-built lookup structures rebuilt lazily when data changes.

/** Transition lookup: toClipId → Transition (avoids O(n) inner loop per clip per frame) */
let _transLookupRef: Transition[] | null = null;
let _transLookup = new Map<string, Transition>();

function getTransitionByToClip(transitions: Transition[]): Map<string, Transition> {
  if (transitions === _transLookupRef) return _transLookup;
  _transLookupRef = transitions;
  _transLookup = new Map(transitions.map((t) => [t.toClipId, t]));
  return _transLookup;
}

/** Lazy-cached asset lookup: assetId → Asset (avoids O(n) Map rebuild per frame) */
let _assetsLookupRef: Asset[] | null = null;
let _assetsLookup = new Map<string, Asset>();

function getAssetsByIdCached(assets: Asset[]): Map<string, Asset> {
  if (assets === _assetsLookupRef) return _assetsLookup;
  _assetsLookupRef = assets;
  _assetsLookup = new Map(assets.map((a) => [a.id, a]));
  return _assetsLookup;
}

/** Lazy-cached clip lookup: clipId → Clip (avoids O(n) Map rebuild per frame) */
let _clipLookupRef: Clip[] | null = null;
let _clipLookup = new Map<string, Clip>();

function getClipsByIdCached(clips: Clip[]): Map<string, Clip> {
  if (clips === _clipLookupRef) return _clipLookup;
  _clipLookupRef = clips;
  _clipLookup = new Map(clips.map((c) => [c.id, c]));
  return _clipLookup;
}

/** Lazy-cached track lookup: trackId → Track (avoids O(n) Map rebuild per frame) */
let _trackLookupRef: Track[] | null = null;
let _trackLookup = new Map<string, Track>();

function getTracksByIdCached(tracks: Track[]): Map<string, Track> {
  if (tracks === _trackLookupRef) return _trackLookup;
  _trackLookupRef = tracks;
  _trackLookup = new Map(tracks.map((t) => [t.id, t]));
  return _trackLookup;
}

/** Shared clip interval cache for active-clip queries */
export const clipCache = new ClipIntervalCache();

// ==================== MAIN SYNC FUNCTIONS ====================

/**
 * Sync video elements with timeline playhead.
 *
 * SIMPLIFIED: This function now ONLY handles video currentTime synchronization.
 * Visibility and transition effects are handled by the new transitions module.
 *
 * @param playhead - Current timeline position in seconds
 * @param clips - All clips on the timeline
 * @param assets - All assets
 * @param videoMap - Map of element IDs to video elements
 * @param setOpacity - Callback to set element visibility (0 or 1)
 * @param isPlaying - Whether timeline is playing
 * @param regionEffect - Optional Region FX effect
 * @param opts - Additional options
 */
export function syncVideosAtPlayhead(
  playhead: number,
  clips: Clip[],
  assets: Asset[],
  videoMap: VideoMap,
  setOpacity: (elementId: string, opacity: number) => void,
  isPlaying: boolean = false,
  regionEffect?: RegionEffect | null,
  opts?: {
    skipElementIds?: Set<string>;
    protectedElementIds?: Set<string>;
    avoidHardSeekElementIds?: Set<string>;
    transitions?: Transition[];
    setTransitionEffect?: (elementId: string, effect: TransitionEffect) => void;
    isMediaReady?: (elementId: string) => boolean;
    isElementLooping?: (elementId: string) => boolean;
    // Optional mute context: when provided, the first play() call sets
    // video.muted to the final effective value so the browser doesn't
    // lock the element into a "muted autoplay" grant that can't be
    // upgraded when syncVideoAudioAtPlayhead later unmutes.
    tracks?: Track[];
    masterMuted?: boolean;
  }
) {
  const assetsById = getAssetsByIdCached(assets);
  const skip = opts?.skipElementIds;
  const transitions = opts?.transitions ?? [];
  const tracksForMute = opts?.tracks;
  const masterMutedForMute = opts?.masterMuted;
  const tracksByIdForMute = tracksForMute ? getTracksByIdCached(tracksForMute) : null;

  // Use cached clip lookup for transition checks
  const clipById = getClipsByIdCached(clips);

  videoSyncState.prevPlayhead = playhead;
  pruneRampMaps();

  // Sync constants (tightened for media-derived clock)
  const SOFT_DRIFT_TOLERANCE = 0.04;       // 40ms (was 80ms)
  const HARD_DRIFT_TOLERANCE = 0.12;       // 120ms (was 300ms)
  const SEEK_COOLDOWN_MS = 500;            // 500ms (was 1500ms)
  const RATE_CORRECTION_MIN = 0.97;        // (was 0.98)
  const RATE_CORRECTION_MAX = 1.03;        // (was 1.02)
  const SCRUB_TOLERANCE = 0.008;           // 8ms (was 16ms)

  const now = performance.now();
  const hasTransitions = transitions.length > 0;
  const transLookup = hasTransitions ? getTransitionByToClip(transitions) : null;

  // Use clip cache for faster active-clip lookup (binary search vs full iteration)
  clipCache.rebuild(clips);
  const maxTransBuf = hasTransitions ? 2 : 0;
  const candidateClips = clipCache.getClipsInRange(
    playhead - PLAYHEAD_WINDOW_EPS,
    playhead + maxTransBuf + PLAYHEAD_WINDOW_EPS
  );
  const syncedVideoElements = new Set<string>();

  for (const c of candidateClips) {
    if (!c.elementId) continue;

    const elId = c.elementId as string;
    const v = videoMap.get(elId);
    if (!v) continue;

    // Skip elements handled by WebCodecs
    if (skip?.has(elId)) {
      if (isTimeInsideClip(c, playhead)) {
        setOpacity(elId, 1);
        syncedVideoElements.add(elId);
      }
      continue;
    }

    // Check if clip is active at this playhead
    const isNormallyActive = isTimeInsideClip(c, playhead);

    // TRANSITION FIX: Check if this clip is the incoming side of an ACTIVE transition window.
    // We only apply this when clips are actually adjacent (touching cut).
    // Fast path: skip transition lookup entirely when no transitions exist.
    let isIncomingInTransition = false;
    let transitionDuration = 0;
    let hasIncomingTransitionOffset = false;
    if (hasTransitions && transLookup) {
      const incomingTrans = transLookup.get(c.id);
      if (incomingTrans) {
        const fromClip = clipById.get(incomingTrans.fromClipId);
        const touchingCut = !!fromClip && Math.abs(fromClip.end - c.start) <= 0.01; // 10ms tolerance
        if (touchingCut) {
          transitionDuration = incomingTrans.duration;
          hasIncomingTransitionOffset = transitionDuration > 0;

          const cutPoint = c.start;
          const halfDuration = transitionDuration / 2;
          const windowStart = cutPoint - halfDuration;
          const windowEnd = cutPoint + halfDuration;
          if (playhead + PLAYHEAD_WINDOW_EPS >= windowStart && playhead <= windowEnd + PLAYHEAD_WINDOW_EPS) {
            isIncomingInTransition = true;
          }
        }
      }
    }

    const isActive = isNormallyActive || isIncomingInTransition;

    if (!isActive) continue;

    syncedVideoElements.add(elId);

    // Calculate target video time
    const clipIn = Number.isFinite(c.in) ? c.in : 0;
    const clipOut = Number.isFinite(c.out) ? c.out : clipIn + Math.max(0.1, c.end - c.start);
    const clipSpeed = (c as any).speed ?? 1;

    // TRANSITION FIX: Keep incoming clip time continuous through the cut point.
    // If this clip has an incoming transition, it starts advancing at (start - duration/2),
    // so time does not snap backwards at playhead = clip.start.
    let effectiveClipStart = c.start;
    if (hasIncomingTransitionOffset) {
      effectiveClipStart = c.start - transitionDuration / 2;
    }

    // Apply Region FX speed if targeting this clip
    const regionTargetsClip =
      !!regionEffect &&
      (regionEffect.targetClipIds?.includes(c.id) ||
        (typeof regionEffect.targetClipId === "string" &&
          regionEffect.targetClipId === c.id));

    const regionSpeedTarget =
      regionTargetsClip && regionEffect?.speed !== undefined
        ? clamp(regionEffect.speed, 0.25, 4)
        : 1;

    const regionSpeed = getRampedValue(
      regionSpeedRamp,
      elId,
      regionSpeedTarget,
      now,
      speedRampMsFor(regionEffect)
    );

    const effectiveSpeed = clipSpeed * regionSpeed;
    // Use effectiveClipStart for incoming clips in transition (adjusted earlier)
    const rel = Math.max(0, playhead - effectiveClipStart);
    const shouldLoop = opts?.isElementLooping?.(elId) ?? false;
    const clipSpan = Math.max(0.001, clipOut - clipIn);
    const clipProgress = rel * effectiveSpeed;
    const targetTime = shouldLoop
      ? clipIn + (clipProgress >= clipSpan ? clipProgress % clipSpan : clipProgress)
      : clipIn + clipProgress;

    // Sync video playback
    const drift = v.currentTime - targetTime;
    const absDrift = Math.abs(drift);
    const lastSeek = videoSyncState.lastSeekTime.get(elId) ?? 0;
    const timeSinceSeek = now - lastSeek;
    const protectLongVideoPlayback = opts?.protectedElementIds?.has(elId) === true;
    const softDriftTolerance = protectLongVideoPlayback ? 0.18 : SOFT_DRIFT_TOLERANCE;
    const hardDriftTolerance = protectLongVideoPlayback ? 1.25 : HARD_DRIFT_TOLERANCE;
    const seekCooldownMs = protectLongVideoPlayback ? 4000 : SEEK_COOLDOWN_MS;

    if (isPlaying) {
      // Transition-active incoming clips are time-critical: seek aggressively at window entry
      // so the clip starts on the correct frame instead of briefly showing stale content.
      if (
        !protectLongVideoPlayback &&
        isIncomingInTransition &&
        absDrift > softDriftTolerance &&
        timeSinceSeek > 80
      ) {
        v.currentTime = targetTime;
        videoSyncState.lastSeekTime.set(elId, now);
        v.playbackRate = effectiveSpeed;
      } else if (
        absDrift > hardDriftTolerance &&
        timeSinceSeek > seekCooldownMs &&
        !v.seeking
      ) {
        // Hard seek needed
        v.currentTime = targetTime;
        videoSyncState.lastSeekTime.set(elId, now);
        v.playbackRate = effectiveSpeed;
      } else if (absDrift > softDriftTolerance) {
        // Soft correction via playbackRate
        const correction = drift > 0 ? RATE_CORRECTION_MIN : RATE_CORRECTION_MAX;
        v.playbackRate = effectiveSpeed * correction;
      } else {
        // In sync
        v.playbackRate = effectiveSpeed;
      }

      // Ensure video is playing.
      // Browsers grant "muted autoplay" when play() is called while muted=true,
      // and that grant does NOT upgrade to audible when muted is later set to
      // false. So: if mute context was passed in, set v.muted to the final
      // effective value BEFORE play(). Without this, the first play of every
      // video element is silent until the clip is replayed. See
      // syncVideoAudioAtPlayhead for the continuous mute/volume sync.
      if (v.paused && v.readyState >= 2) {
        if (tracksByIdForMute) {
          const track = tracksByIdForMute.get(c.trackId);
          const trackMuted = !!track?.muted;
          const clipMuted = !!(c as any).muted;
          const removeAudio = !!(c as any).removeOriginalAudio;
          const hasSolo = tracksForMute!.some(
            (t) => (t.kind === "audio" || t.kind === "video") && (t as any).solo
          );
          const trackSolo = !!(track as any)?.solo;
          const shouldMute =
            !!masterMutedForMute ||
            trackMuted ||
            clipMuted ||
            removeAudio ||
            (hasSolo && !trackSolo);
          if (v.muted !== shouldMute) v.muted = shouldMute;
        }
        v.play().catch(() => {});
      }
    } else {
      // Paused/scrubbing: try the cached frame first — instant display,
      // no seek required. D4 staging: if we find a bitmap within 50ms of
      // the target, stage it via setScrubBitmap so the canvas renderer
      // picks it up on the next draw. We still issue the underlying seek
      // so the next decoded frame is accurate when the user stops
      // scrubbing and the live video becomes authoritative again.
      const cached = getCachedFrameNear(elId, targetTime, 0.05);
      if (cached) {
        setScrubBitmap(elId, cached);
      } else {
        clearScrubBitmap(elId);
      }

      // Paused/scrubbing: seek to exact position.
      // Skip if a previous seek is still pending — on slow hardware this prevents
      // decoder thrashing (issuing a new seek while the decoder hasn't finished the last one
      // causes it to abort and restart, making scrub feel laggy and jumpy).
      if (absDrift > SCRUB_TOLERANCE && !v.seeking) {
        v.currentTime = targetTime;
      }
      if (!v.paused) {
        try {
          v.pause();
        } catch {}
      }
      v.playbackRate = effectiveSpeed;
    }

    // Hide clip until browser has a decodable frame ready.
    // During play: only gate on incoming transition clips (avoid hiding active clips mid-playback).
    // During scrub/pause: gate ALL clips — prevents stale frame from previous seek flashing on screen.
    const readyForDisplay = isPlaying
      ? (!isIncomingInTransition || v.readyState >= 2)
      : v.readyState >= 2;
    setOpacity(elId, readyForDisplay ? 1 : 0);
  }

  // Hide/pause video elements not covered by any active candidate clip
  for (const [elId, v] of videoMap) {
    if (syncedVideoElements.has(elId)) continue;
    if (skip?.has(elId)) continue;
    setOpacity(elId, 0);
    if (!v.paused) {
      try { v.pause(); } catch {}
    }
  }
}

/**
 * Sync non-video element visibility based on clip timing.
 *
 * SIMPLIFIED: Uses strict clip lifetime - visible only when playhead is inside clip.
 * No hold states, no freeze frames, no transition logic.
 */
export function syncElementVisibilityAtPlayhead(
  playhead: number,
  clips: Clip[],
  assets: Asset[],
  setVisible: (elementId: string, visible: boolean) => void,
  allElementIds?: string[],
  excludeElementIds?: Set<string>
) {
  const assetsById = getAssetsByIdCached(assets);

  // Track video elements (handled by syncVideosAtPlayhead)
  const videoElementIds = new Set<string>();

  // Track elements bound to timeline
  const timelineBoundElementIds = new Set<string>();

  // Group non-video clips by element
  const byElement = new Map<string, Clip[]>();
  for (const c of clips) {
    const elId = c.elementId as string | undefined;
    if (!elId) continue;

    timelineBoundElementIds.add(elId);

    const asset = assetsById.get(c.assetId);
    if (!asset) continue;

    if (asset.type === "video") {
      videoElementIds.add(elId);
      continue;
    }

    const arr = byElement.get(elId) ?? [];
    arr.push(c);
    byElement.set(elId, arr);
  }

  // Determine visibility for each non-video element
  for (const [elId, list] of byElement.entries()) {
    const isVisible = list.some((c) => isTimeInsideClip(c, playhead));
    setVisible(elId, isVisible);
  }

  // Hide orphaned elements that are timeline-bound but have no active clips
  if (allElementIds) {
    for (const elId of allElementIds) {
      if (videoElementIds.has(elId)) continue;
      if (excludeElementIds?.has(elId)) continue;
      if (byElement.has(elId)) continue; // Already processed

      // Element was timeline-bound but has no clips now - hide it
      if (timelineBoundElementIds.has(elId)) {
        setVisible(elId, false);
      }
    }
  }
}

/**
 * Sync video element audio (mute/volume) based on clip and track settings.
 */
export function syncVideoAudioAtPlayhead(
  playhead: number,
  clips: Clip[],
  assets: Asset[],
  tracks: Track[],
  videoMap: VideoMap,
  masterMuted: boolean,
  isPlaying: boolean,
  regionEffect?: RegionEffect | null,
  opts?: {
    skipElementIds?: Set<string>;
  }
) {
  const assetsById = getAssetsByIdCached(assets);
  const tracksById = getTracksByIdCached(tracks);
  const skip = opts?.skipElementIds;
  const managedVideoElementIds = new Set<string>();
  const activeAudioByElement = new Map<string, { video: HTMLVideoElement; volume: number }>();

  for (const c of clips) {
    if (!c.elementId) continue;
    const elId = c.elementId as string;
    if (skip?.has(elId)) continue;

    const v = videoMap.get(elId);
    if (!v) continue;

    const asset = assetsById.get(c.assetId);
    if (!asset || asset.type !== "video") continue;
    managedVideoElementIds.add(elId);

    const isActive = isTimeInsideClip(c, playhead);
    if (!isActive || !isPlaying) continue;

    // Determine mute state
    const track = tracksById.get(c.trackId);
    const trackMuted = track?.muted ?? false;
    const clipMuted = (c as any).muted ?? false;
    const removeAudio = (c as any).removeOriginalAudio ?? false;

    if (masterMuted || trackMuted || clipMuted || removeAudio) {
      continue;
    }

    // Calculate volume
    const clipVolume = ((c as any).audio?.volume ?? 100) / 100;
    const trackVolume = ((track as any)?.volume ?? 100) / 100;

    // Apply Region FX volume if targeting this clip
    const regionTargetsClip =
      !!regionEffect &&
      (regionEffect.targetClipIds?.includes(c.id) ||
        (typeof regionEffect.targetClipId === "string" &&
          regionEffect.targetClipId === c.id));

    let regionVolume = 1;
    if (regionTargetsClip && regionEffect?.volume !== undefined) {
      const regionVolTarget = clamp(regionEffect.volume, 0, 2);
      const audioNow = performance.now();
      regionVolume = getRampedValue(
        regionVolumeRamp,
        elId,
        regionVolTarget,
        audioNow,
        volumeRampMsFor(regionEffect)
      );
    }

    const baseVolume = clipVolume * trackVolume * regionVolume;
    const finalVolume = calculateVolumeWithFades(c, playhead, baseVolume);
    const existing = activeAudioByElement.get(elId);
    if (!existing || finalVolume > existing.volume) {
      activeAudioByElement.set(elId, {
        video: v,
        volume: clamp(finalVolume, 0, 1),
      });
    }
  }

  for (const elementId of managedVideoElementIds) {
    const video = videoMap.get(elementId);
    if (!video) continue;

    const active = activeAudioByElement.get(elementId);
    if (!active || !isPlaying) {
      // Debounce mute: wait MUTE_DEBOUNCE_FRAMES consecutive inactive
      // frames before muting. This prevents the single-frame mute/unmute
      // flip-flop at clip boundaries that causes audible dropouts.
      const prevCount = inactiveFrameCount.get(elementId) ?? 0;
      const newCount = prevCount + 1;
      inactiveFrameCount.set(elementId, newCount);
      if (newCount >= MUTE_DEBOUNCE_FRAMES && !video.muted) {
        video.muted = true;
      }
      continue;
    }
    // Active — reset inactive counter
    inactiveFrameCount.set(elementId, 0);

    const wasMuted = video.muted;
    video.muted = false;
    video.volume = active.volume;

    // Re-arm audio when transitioning from muted OR paused.
    // Safari/Firefox retain a "muted autoplay" grant after a muted play()
    // and do NOT re-enable audio on a subsequent muted=false alone, so the
    // element keeps playing silently unless play() is re-issued. Checking
    // wasMuted catches that case; the pause check catches fresh plays.
    if ((wasMuted || video.paused) && video.readyState >= 2) {
      video.play().catch(() => {});
    }
  }
}

// ==================== LEGACY EXPORTS (for backward compatibility) ====================

/**
 * @deprecated No longer needed - visibility is simple now
 */
export function clearLastFrameHoldState() {
  // No-op - hold state removed
}

/**
 * @deprecated Use isPlayheadInGap from '../transitions' instead
 */
export function isPlayheadInGap(playhead: number, clips: Clip[]): boolean {
  return !clips.some((c) => isTimeInsideClip(c, playhead));
}
